import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Calendar, Loader2, Play, Stethoscope } from 'lucide-react';
import { useAtendimento } from '@/hooks/useAtendimentos';
import { useStartClinicalEncounter } from '@/hooks/useConsultasClinicas';
import { usePaciente } from '@/hooks/usePaciente';
import { AtributoList } from '@/components/atendimento/AtributoList';
import { PacienteHeader } from '@/components/paciente/PacienteHeader';
import { LoadingState } from '@/components/shared/LoadingState';
import { ErrorState } from '@/components/shared/ErrorState';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { classificarProcedimento, formatarData } from '@/lib/utils';

export const Route = createFileRoute('/pep-vida/_authed/atendimentos/$cdAtendimento')({
  component: AtendimentoDetailPage,
});

function AtendimentoDetailPage() {
  const { cdAtendimento } = Route.useParams();
  const cd = Number(cdAtendimento);
  const navigate = useNavigate();

  const { data: atendimento, isLoading, error, refetch } = useAtendimento(cd);
  const { data: paciente } = usePaciente(atendimento ? atendimento.cdPaciente : null);
  const startEncounter = useStartClinicalEncounter();

  if (isLoading) return <LoadingState />;
  if (error) {
    if ((error as Error).message.includes('nao encontrado')) throw notFound();
    return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  }
  if (!atendimento) throw notFound();

  const tipo = classificarProcedimento(atendimento.dsProcedimento);

  const handleStartEncounter = async () => {
    const encounter = await startEncounter.mutateAsync({
      cd_atendimento: atendimento.cdAtendimento,
      cd_paciente: atendimento.cdPaciente,
    });
    navigate({ to: '/pep-vida/consultas/$id', params: { id: encounter.id } });
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="atendimento-detail">
      <Link to="/pep-vida/dashboard">
        <Button variant="ghost" size="sm">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar para fila
        </Button>
      </Link>
      {paciente && <PacienteHeader paciente={paciente} />}
      <Card variant={tipo}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold">{atendimento.dsProcedimento || 'Atendimento'}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatarData(atendimento.dtData)}
                </span>
                {atendimento.dsModalidade && (
                  <span className="inline-flex items-center gap-1">
                    <Stethoscope className="h-3.5 w-3.5" />
                    {atendimento.dsModalidade}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center">
              <Badge variant={tipo} className="capitalize">{tipo}</Badge>
              <Button
                size="sm"
                onClick={handleStartEncounter}
                disabled={startEncounter.isPending}
                data-testid="iniciar-consulta"
              >
                {startEncounter.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                Iniciar consulta
              </Button>
            </div>
          </div>
          {startEncounter.error && (
            <p className="mt-3 text-sm text-destructive">
              {(startEncounter.error as Error).message}
            </p>
          )}
        </CardContent>
      </Card>
      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Documentos do prontuario
        </h3>
        <AtributoList
          cdAtendimento={atendimento.cdAtendimento}
          atributos={atendimento.atributos || []}
        />
      </section>
    </div>
  );
}
